import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { Commands, Downloads, Results } from '@local/client-contracts';
import { isNativeWindow } from '@local/common-web';
import { PopupRef, UiIconModel } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ContextMenuComponent, ContextMenuData, ContextMenuItem, ContextMenuService } from '@shared/components';
import { DragData, DragMetadata } from '@shared/directives/drag-and-drop.directive';
import { EventsService } from '@shared/services';
import { DownloadsService } from '@shared/services/downloads.service';
import { FlagsService } from '@shared/services/flags.service';
import { isDownloadUrlCommand, isOpenUrlCommand, isRightClick, join } from '@shared/utils';
import { getIconModel } from '@shared/utils/set-icon.util';
import { isEqual } from 'lodash';
import { Subscription } from 'rxjs';
import { DropdownComponent } from 'src/app/bar/components';
import { CommandsService } from 'src/app/bar/services/commands/commands.service';
import { HubService } from 'src/app/bar/services/hub.service';

const actionToIcon: Record<string, string> = {
  open: 'icon-launch',
  'open-url': 'icon-network',
  'open-desktop': 'icon-cube',
  download: 'icon-download2',
};

@UntilDestroy()
@Component({
  selector: 'result-tag',
  templateUrl: './result-tag.component.html',
  styleUrls: ['./result-tag.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResultTagComponent implements AfterViewInit {
  @Input() set model(value: Results.Tag) {
    if (isEqual(value, this._model)) return;
    this.dragData.model = value;
    this._model = value;
    this.cdr.markForCheck();
  }

  @Input() set dragMetadata(metadata: DragMetadata) {
    this.dragData.metadata = metadata;
  }
  @Input() defaultBackgroundColor: string;
  @Output() invoke = new EventEmitter<Commands.Command>();

  backgroundColor: string = '';
  contextMenuOpened: boolean = false;

  @ViewChild('dropdown') dropdown: DropdownComponent;

  @ViewChild('wrapperContainer') wrapperContainer: ElementRef;
  @ViewChild('tag') tag: ElementRef;

  iconModel: UiIconModel;
  dragData: DragData = { metadata: undefined, model: undefined };

  get model(): Results.Tag {
    return this._model;
  }

  private _model: Results.Tag;
  private readonly isNativeWindow = isNativeWindow();

  showCopied: boolean;
  tooltipText: string;

  activeContextMenu: PopupRef<ContextMenuComponent, ContextMenuData>;
  constructor(
    private cdr: ChangeDetectorRef,
    private contextMenuService: ContextMenuService,
    private downloadsService: DownloadsService,
    private commandsService: CommandsService,
    private eventService: EventsService,
    private hubService: HubService,
    private flagsService: FlagsService
  ) {}

  ngAfterViewInit(): void {
    if (this.model.text?.startsWith('+')) {
      this.tooltipText = this.model.tooltip || this.model.caption;
    } else {
      this.tooltipText =
        this.wrapperContainer?.nativeElement.offsetWidth < this.tag?.nativeElement.offsetWidth
          ? this.model.tooltip || this.model.caption
          : '';
    }
    this.cdr.markForCheck();
    const icon = this.model?.icon;
    this.iconModel = getIconModel(icon);
  }

  @HostListener('auxclick', ['$event']) onAuxClick(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    if (isRightClick(event)) this.openContextMenu(event);
  }
  @HostListener('click')
  async onClick(): Promise<void> {
    const downloadUrl: Commands.DownloadUrl | null = isDownloadUrlCommand(this.dragData.model?.onDrag) && this.dragData.model?.onDrag;
    if (!downloadUrl) {
      this.sendCommand(this.dragData.model?.onClick);
      return;
    }

    if (!(await this.isDownloadEnabled())) {
      return;
    }

    this.sendDownloadEvents();
    if (!this.isNativeWindow) {
      const req: Downloads.ResourceDownloadRequest = {
        type: 'Resource',
        url: downloadUrl.url,
        resource: this.dragData.metadata?.resource,
        icon: this.dragData.metadata?.icon,
      };
      await this.downloadsService.resourceDownload(req);
    } else {
      const id: string = await this.downloadsService.getId(downloadUrl.url, this.dragData.metadata?.resource?.linkId);
      const opened = await this.openDownload(id);
      if (opened) return;
      this.openDownloadOnCompleted(id);
    }
  }

  @HostListener('contextmenu', ['$event'])
  onRightClick(event: MouseEvent): void {
    event.stopImmediatePropagation();
    event.preventDefault();
    this.openContextMenu(event);
  }

  sendCommand(command: Commands.Command): void {
    if (!command) {
      return;
    }

    this.invoke.emit(command);
    if (command.type === 'copy-clipboard') {
      this.showCopied = true;
      this.cdr.markForCheck();
      setTimeout(() => {
        this.showCopied = false;
        this.cdr.markForCheck();
      }, 2000);
    }
  }

  private async openContextMenu(event: MouseEvent) {
    if (!this.dragData.model || !(this.dragData.model as Results.Tag).onDrag) return;
    let openAt: { x: number; y: number } = event;
    const items = await this.buildContextMenuItems();
    this.activeContextMenu = this.contextMenuService.open(
      openAt,
      {
        items,
        onInvoke: (item: ContextMenuItem) => this.handleContextMenuCommand(item.command),
      },
      { position: 'below' }
    );
    this.contextMenuOpened = true;
    this.cdr.markForCheck();
    this.subscribeToContextMenuClosed();
  }

  subscribeToContextMenuClosed() {
    this.activeContextMenu.close$.pipe(untilDestroyed(this)).subscribe(() => {
      this.contextMenuOpened = false;
      this.cdr.markForCheck();
    });
  }

  private async openDownload(id: string): Promise<boolean> {
    const download: Downloads.Download = this.downloadsService.all[id];
    const downloaded: boolean = download && download.status === 'completed';
    if (downloaded) {
      const command: Commands.OpenUrl = { type: 'open-url', url: join(download.path, download.name) };
      await this.commandsService.executeCommand(command, {});
      return true;
    }
    return false;
  }

  private async openDownloadOnCompleted(id: string): Promise<void> {
    await this.downloadsService.setVisibility(id, 'visible');
    const subscription: Subscription = this.downloadsService.downloadsChange.subscribe(async () => {
      const download = this.downloadsService.all[id] as Downloads.ResourceDownload;
      const downloaded: boolean = download?.status === 'completed';
      if (downloaded) {
        const command: Commands.OpenUrl = { type: 'open-url', url: join(download.path, download.name) };
        await this.commandsService.executeCommand(command, {
          resource: { id: download.resource.id, kind: 'link-resource' },
          linkId: download.resource.linkId,
        });
        subscription.unsubscribe();
      }
    });
  }

  async handleContextMenuCommand(command): Promise<void> {
    const { url } = command;
    const metadata: DragMetadata = this.dragData?.metadata;
    const downloadUrl = isDownloadUrlCommand(this.dragData.model?.onDrag) && this.dragData.model?.onDrag?.url;
    const id: string = await this.downloadsService.getId(downloadUrl, metadata?.resource?.linkId);
    const download: Downloads.Download = this.downloadsService.all[id];

    if (isDownloadUrlCommand(command)) {
      if (download) {
        if (download.visibility !== 'visible') this.downloadsService.setVisibility(download.id, 'visible');
      } else {
        const req: Downloads.ResourceDownloadRequest = {
          type: 'Resource',
          url,
          resource: this.dragData.metadata?.resource,
          icon: this.dragData.metadata?.icon,
        };
        await this.downloadsService.resourceDownload(req);
      }
      this.sendDownloadEvents();
    } else if (isOpenUrlCommand(command)) {
      const opened: boolean = await this.openDownload(id);
      if (opened) return;
      this.openDownloadOnCompleted(id);
      this.sentOpenEvent();
      this.invoke.emit(command);
    }
  }

  private async buildContextMenuItems(): Promise<ContextMenuItem[]> {
    const items: ContextMenuItem[] = [];

    const tag = this.dragData.model as Results.Tag;
    const downloadUrl: Commands.DownloadUrl | null =
      tag.onDrag && isDownloadUrlCommand(tag.onDrag) && (await this.isDownloadEnabled()) ? tag.onDrag : null;
    const openUrl: Commands.OpenUrl | null = isOpenUrlCommand(tag.onClick) ? tag.onClick : null;
    const metadata: DragMetadata = this.dragData?.metadata;
    let downloaded = false;
    let download: Downloads.Download;
    if (downloadUrl) {
      const id: string = await this.downloadsService.getId(downloadUrl.url, metadata?.resource?.linkId);
      download = this.downloadsService.all[id];
      downloaded = download?.status === 'completed';
    }

    if (openUrl && this.isNativeWindow) {
      const { url } = downloaded ? downloadUrl : openUrl;

      items.push({
        id: 'open',
        text: 'Open',
        data: { url },
        command: { type: 'open-url', url } as Commands.OpenUrl,
        icon: { type: 'font-icon', value: actionToIcon['open'] },
      });
    }

    if (downloadUrl && (!download || download.visibility !== 'visible')) {
      const url = downloadUrl.url;

      items.push({
        id: 'download',
        text: 'Download',
        data: { url },
        command: { type: 'download-url', url } as Commands.DownloadUrl,
        icon: { type: 'font-icon', value: actionToIcon['download'] },
      });
    }
    return items;
  }

  private get eventResources() {
    const resource = this.dragData?.metadata?.resource;
    const info = this.dragData?.metadata?.eventInfo;
    return [{ id: resource.id, appId: resource.appId, type: resource.type, position: info?.position, list: info?.list }];
  }

  private sendDownloadEvents() {
    this.eventService.event('results_tags.download', {
      resources: this.eventResources,
      location: { title: this.hubService.currentLocation },
      search: this.dragData?.metadata?.eventInfo?.search,
      target: 'mouse_click',
    });
    this.eventService.event('results_tags.action', {
      resources: this.eventResources,
      location: { title: this.hubService.currentLocation },
      search: this.dragData?.metadata?.eventInfo?.search,
      label: 'download',
    });
  }

  private sentOpenEvent() {
    this.eventService.event('results_tags.open', {
      resources: this.eventResources,
      location: { title: this.hubService.currentLocation },
      search: this.dragData?.metadata?.eventInfo?.search,
      target: 'mouse_click',
    });
  }

  private isDownloadEnabled(): Promise<boolean> {
    return this.flagsService.isEnabled('filePreviews', this.dragData.metadata?.resource?.linkId);
  }
}
